import {AccessGroup} from './accessGroup';
import {Amenity} from './amenity';
import {Building} from './building';
import {GeoLocation} from './geoLocation';

/**
 * 对讲机设备
 * @export
 * @interface IntercomDevice
 */
export interface IntercomDevice {
  /**
   * 权限组
   * @type {Array<AccessGroup>}
   * @memberof IntercomDevice
   */
  accessGroup?: Array<AccessGroup>;
  /**
   *
   * @type {IntercomDeviceApartment}
   * @memberof IntercomDevice
   */
  apartment?: IntercomDeviceApartment;
  /**
   * 公寓 id
   * @type {string}
   * @memberof IntercomDevice
   */
  apartmentId?: string;
  /**
   *
   * @type {IntercomDeviceBuilding}
   * @memberof IntercomDevice
   */
  building?: IntercomDeviceBuilding;
  /**
   * 所属公寓楼
   * @type {string}
   * @memberof IntercomDevice
   */
  buildingId?: string;
  /**
   * @type {string}
   * @memberof IntercomDevice
   */
  amenityId?: string;
  /**
   * 创建日期
   * @type {string}
   * @memberof IntercomDevice
   */
  createdAt?: string;
  /**
   * 设备详情
   * @type {object}
   * @memberof IntercomDevice
   */
  detail?: object;
  /**
   * 设备编号
   * @type {string}
   * @memberof IntercomDevice
   */
  deviceId?: string;
  /**
   *
   * @type {string}
   * @memberof IntercomDevice
   */
  id?: string;
  /**
   * 安装时间
   * @type {string}
   * @memberof IntercomDevice
   */
  installedOn?: string;
  /**
   * 设备名字
   * @type {string}
   * @memberof IntercomDevice
   */
  name?: string;
  /**
   * 所属组织
   * @type {string}
   * @memberof IntercomDevice
   */
  orgId?: string;
  /**
   * 设备状态
   * @type {string}
   * @memberof IntercomDevice
   */
  status?: IntercomDeviceStatusEnum;
  /**
   * - 默认类型: DEVICE_TYPE_UNKNOWN - 对讲机: DEVICE_TYPE_INTERCOM - 扬声器: DEVICE_TYPE_SPEAKER - 门禁: DEVICE_TYPE_AccessControl
   * @type {string}
   * @memberof IntercomDevice
   */
  type?: IntercomDeviceTypeEnum;
  /**
   * 修改日期
   * @type {string}
   * @memberof IntercomDevice
   */
  updatedAt?: string;
  /**
   * MAC Address
   * @type {string}
   * @memberof IntercomDevice
   */
  macAddress?: string;
  /**
   * 时区
   * @type {string}
   * @memberof IntercomDevice
   */
  timeZone?: string;
  /**
   * Location verification enabled
   * @type {boolean}
   * @memberof IntercomDevice
   */
  locationVerificationEnabled?: boolean;
  /**
   * Event photo capture enabled
   * @type {boolean}
   * @memberof IntercomDevice
   */
  eventPhotoCaptureEnabled?: boolean;
  /**
   * Call timeout in seconds (10-180). When the device places a call and no
   * one joins within this window the call hangs up automatically.
   * @type {number}
   * @memberof IntercomDevice
   */
  intercomTimeoutSeconds?: number;
  /**
   * Device Type (PHYSICAL or VIRTUAL)
   * @type {string}
   * @memberof IntercomDevice
   */
  deviceType?: string;
  /**
   * Visitor QR intercom URL. Present on virtual device detail only.
   * @type {string}
   * @memberof IntercomDevice
   */
  qrUrl?: string | null;
  /**
   * When the visitor QR secret was generated.
   * @type {string}
   * @memberof IntercomDevice
   */
  qrGeneratedAt?: string | null;
  /**
   * Linked door integration: PDK, Brivo, or ControlByWeb
   * @type {string}
   * @memberof IntercomDevice
   */
  integration?: string | null;
  managers?: Array<{
    id: string;
    name: string;
    email: string;
    userType: 'TENANT_USER' | 'ORG_USER';
  }>;
}

export const IntercomDeviceStatusEnum = {
  Unknown: 'DEVICE_STATUS_UNKNOWN',
  Online: 'DEVICE_STATUS_ONLINE',
  Offline: 'DEVICE_STATUS_OFFLINE',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type IntercomDeviceStatusEnum =
  (typeof IntercomDeviceStatusEnum)[keyof typeof IntercomDeviceStatusEnum];
export const IntercomDeviceTypeEnum = {
  Unknown: 'DEVICE_TYPE_UNKNOWN',
  Intercom: 'DEVICE_TYPE_INTERCOM',
  Speaker: 'DEVICE_TYPE_SPEAKER',
  AccessControl: 'DEVICE_TYPE_AccessControl',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type IntercomDeviceTypeEnum =
  (typeof IntercomDeviceTypeEnum)[keyof typeof IntercomDeviceTypeEnum];

/**
 * 公寓
 * @export
 * @interface IntercomDeviceApartment
 */
export interface IntercomDeviceApartment {
  /**
   *
   * @type {Array<Amenity>}
   * @memberof IntercomDeviceApartment
   */
  amenities?: Array<Amenity>;
  /**
   *
   * @type {Array<Building>}
   * @memberof IntercomDeviceApartment
   */
  buildings?: Array<Building>;
  /**
   *
   * @type {string}
   * @memberof IntercomDeviceApartment
   */
  city?: string;
  /**
   * 创建日期
   * @type {string}
   * @memberof IntercomDeviceApartment
   */
  createdAt?: string;
  /**
   * 公寓描述信息
   * @type {string}
   * @memberof IntercomDeviceApartment
   */
  description?: string;
  /**
   *
   * @type {string}
   * @memberof IntercomDeviceApartment
   */
  id?: string;
  /**
   * 公寓位置
   * @type {GeoLocation}
   * @memberof IntercomDeviceApartment
   */
  location?: GeoLocation;
  /**
   * 公寓名字
   * @type {string}
   * @memberof IntercomDeviceApartment
   */
  name?: string;
  /**
   * 所属组织
   * @type {string}
   * @memberof IntercomDeviceApartment
   */
  orgId?: string;
  /**
   * 站点
   * @type {string}
   * @memberof IntercomDeviceApartment
   */
  siteId?: string;
  /**
   *
   * @type {string}
   * @memberof IntercomDeviceApartment
   */
  state?: string;
  /**
   *
   * @type {string}
   * @memberof IntercomDeviceApartment
   */
  street?: string;
  /**
   * 修改日期
   * @type {string}
   * @memberof IntercomDeviceApartment
   */
  updatedAt?: string;
  /**
   *
   * @type {string}
   * @memberof IntercomDeviceApartment
   */
  zipCode?: string;
}
/**
 * 公寓楼
 * @export
 * @interface IntercomDeviceBuilding
 */
export interface IntercomDeviceBuilding {
  /**
   * 公寓
   * @type {string}
   * @memberof IntercomDeviceBuilding
   */
  apartmentId?: string;
  /**
   * 创建日期
   * @type {string}
   * @memberof IntercomDeviceBuilding
   */
  createdAt?: string;
  /**
   * 描述信息
   * @type {string}
   * @memberof IntercomDeviceBuilding
   */
  description?: string;
  /**
   * 楼层数
   * @type {number}
   * @memberof IntercomDeviceBuilding
   */
  floorNum?: number;
  /**
   * 每层房间数
   * @type {number}
   * @memberof IntercomDeviceBuilding
   */
  floorUnitCount?: number;
  /**
   *
   * @type {string}
   * @memberof IntercomDeviceBuilding
   */
  id?: string;
  /**
   * 公寓楼名字
   * @type {string}
   * @memberof IntercomDeviceBuilding
   */
  name?: string;
  /**
   * 所属组织
   * @type {string}
   * @memberof IntercomDeviceBuilding
   */
  orgId?: string;
  /**
   * 修改日期
   * @type {string}
   * @memberof IntercomDeviceBuilding
   */
  updatedAt?: string;
}
/**
 *
 * @export
 * @interface IntercomDeviceCreateReq
 */
export interface IntercomDeviceCreateReq {
  /**
   *
   * @type {IntercomDevice}
   * @memberof IntercomDeviceCreateReq
   */
  intercomDevice?: IntercomDevice;
}
/**
 *
 * @export
 * @interface IntercomDeviceRegisterReq
 */
export interface IntercomDeviceRegisterReq {
  /**
   *
   * @type {string}
   * @memberof IntercomDeviceRegisterReq
   */
  macAddress?: string;
  /**
   *
   * @type {string}
   * @memberof IntercomDeviceRegisterReq
   */
  publicKey?: string;
}
/**
 *
 * @export
 * @interface IntercomDeviceUpdateReq
 */
export interface IntercomDeviceUpdateReq {
  /**
   *
   * @type {IntercomDevice}
   * @memberof IntercomDeviceUpdateReq
   */
  intercomDevice?: IntercomDevice;
  /**
   *
   * @type {string}
   * @memberof IntercomDeviceUpdateReq
   */
  intercomDeviceId?: string;
}
/**
 *
 * @export
 * @interface IntercomDevices
 */
export interface IntercomDevices {
  /**
   *
   * @type {Array<IntercomDevice>}
   * @memberof IntercomDevices
   */
  intercomDevices?: Array<IntercomDevice>;
  /**
   *
   * @type {number}
   * @memberof IntercomDevices
   */
  total?: number;
}

/**
 * Intercom Manager 分配请求
 * @export
 * @interface IntercomManagerAssignReq
 */
export interface IntercomManagerAssignReq {
  /**
   * 设备 ID
   * @type {string}
   * @memberof IntercomManagerAssignReq
   */
  deviceId: string;

  /**
   * 用户 ID
   * @type {string}
   * @memberof IntercomManagerAssignReq
   */
  userId: string;
}

/**
 * Intercom Manager 分配响应
 * @export
 * @interface IntercomManagerAssignRsp
 */
export interface IntercomManagerAssignRsp {
  /**
   * 设备 ID
   * @type {string}
   * @memberof IntercomManagerAssignRsp
   */
  intercom_device_id: string;

  /**
   * 用户 ID
   * @type {string}
   * @memberof IntercomManagerAssignRsp
   */
  user_id: string;

  /**
   * 创建时间
   * @type {string}
   * @memberof IntercomManagerAssignRsp
   */
  created_at: string;

  /**
   * 更新时间
   * @type {string}
   * @memberof IntercomManagerAssignRsp
   */
  updated_at: string;

  /**
   * 用户信息
   * @type {object}
   * @memberof IntercomManagerAssignRsp
   */
  entrio_users: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
}

/**
 * Intercom Manager 编辑响应
 * @export
 * @interface IntercomManagerEditRsp
 */
export interface IntercomManagerEditRsp {
  /**
   * 用户 ID
   * @type {string}
   * @memberof IntercomManagerEditRsp
   */
  userId: string;

  /**
   * 名
   * @type {string}
   * @memberof IntercomManagerEditRsp
   */
  firstName: string;

  /**
   * 姓
   * @type {string}
   * @memberof IntercomManagerEditRsp
   */
  lastName: string;

  /**
   * 邮箱
   * @type {string}
   * @memberof IntercomManagerEditRsp
   */
  email: string;

  /**
   * 分配时间
   * @type {string}
   * @memberof IntercomManagerEditRsp
   */
  assignedAt: string;
}

/**
 * Intercom Manager 取消分配响应
 * @export
 * @interface IntercomManagerUnassignRsp
 */
export interface IntercomManagerUnassignRsp {
  /**
   * 设备 ID
   * @type {string}
   * @memberof IntercomManagerUnassignRsp
   */
  deviceId: string;

  /**
   * 用户 ID
   * @type {string}
   * @memberof IntercomManagerUnassignRsp
   */
  userId: string;

  /**
   * 取消分配时间
   * @type {string}
   * @memberof IntercomManagerUnassignRsp
   */
  unassignedAt: string;
}

/**
 * Intercom Manager 批量分配请求
 * @export
 * @interface IntercomManagerBulkAssignReq
 */
export interface IntercomManagerBulkAssignReq {
  /**
   * 用户 ID 列表
   * @type {Array<string>}
   * @memberof IntercomManagerBulkAssignReq
   */
  userIds: Array<string>;
}

/**
 * Intercom Manager 批量分配结果项
 * @export
 * @interface IntercomManagerBulkAssignResultItem
 */
export interface IntercomManagerBulkAssignResultItem {
  /**
   * 用户 ID
   * @type {string}
   * @memberof IntercomManagerBulkAssignResultItem
   */
  userId: string;

  /**
   * 管理员信息
   * @type {IntercomManagerAssignRsp}
   * @memberof IntercomManagerBulkAssignResultItem
   */
  manager: IntercomManagerAssignRsp;
}

/**
 * Intercom Manager 批量分配失败项
 * @export
 * @interface IntercomManagerBulkAssignFailedItem
 */
export interface IntercomManagerBulkAssignFailedItem {
  /**
   * 用户 ID
   * @type {string}
   * @memberof IntercomManagerBulkAssignFailedItem
   */
  userId: string;

  /**
   * 错误信息
   * @type {string}
   * @memberof IntercomManagerBulkAssignFailedItem
   */
  error: string;
}

/**
 * Intercom Manager 批量分配响应
 * @export
 * @interface IntercomManagerBulkAssignRsp
 */
export interface IntercomManagerBulkAssignRsp {
  /**
   * 操作是否成功
   * @type {boolean}
   * @memberof IntercomManagerBulkAssignRsp
   */
  success: boolean;

  /**
   * 操作信息
   * @type {string}
   * @memberof IntercomManagerBulkAssignRsp
   */
  message: string;

  /**
   * 操作结果
   * @type {object}
   * @memberof IntercomManagerBulkAssignRsp
   */
  results: {
    /**
     * 成功分配的管理员列表
     * @type {Array<IntercomManagerBulkAssignResultItem>}
     */
    succeeded: Array<IntercomManagerBulkAssignResultItem>;

    /**
     * 分配失败的列表
     * @type {Array<IntercomManagerBulkAssignFailedItem>}
     */
    failed: Array<IntercomManagerBulkAssignFailedItem>;
  };
}

/**
 * Intercom Manager 替换请求
 * @export
 * @interface IntercomManagerReplaceReq
 */
export interface IntercomManagerReplaceReq {
  /**
   * 用户 ID 列表
   * @type {Array<string>}
   * @memberof IntercomManagerReplaceReq
   */
  userIds: Array<string>;
}

/**
 * Intercom Manager 替换响应
 * @export
 * @interface IntercomManagerReplaceRsp
 */
export interface IntercomManagerReplaceRsp {
  /**
   * 操作是否成功
   * @type {boolean}
   * @memberof IntercomManagerReplaceRsp
   */
  success: boolean;

  /**
   * 操作信息
   * @type {string}
   * @memberof IntercomManagerReplaceRsp
   */
  message: string;

  /**
   * 当前管理员列表
   * @type {Array<IntercomManagerAssignRsp>}
   * @memberof IntercomManagerReplaceRsp
   */
  managers: Array<IntercomManagerAssignRsp>;
}

/**
 * Device Module Type Enum
 * @export
 */
export const DeviceModuleTypeEnum = {
  Directory: 'DIRECTORY',
  Delivery: 'DELIVERY',
  DoorPin: 'DOOR_PIN',
  CallReception: 'CALL_RECEPTION',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type DeviceModuleTypeEnum = (typeof DeviceModuleTypeEnum)[keyof typeof DeviceModuleTypeEnum];

/**
 * Device Module Device Types Enum
 * @export
 */
export const DeviceModuleDeviceTypeEnum = {
  Virtual: 'VIRTUAL',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type DeviceModuleDeviceTypeEnum =
  (typeof DeviceModuleDeviceTypeEnum)[keyof typeof DeviceModuleDeviceTypeEnum];

/**
 * Device Module Creator
 * @export
 * @interface DeviceModuleCreator
 */
export interface DeviceModuleCreator {
  /**
   * User ID
   * @type {string}
   * @memberof DeviceModuleCreator
   */
  id: string;

  /**
   * User Name
   * @type {string}
   * @memberof DeviceModuleCreator
   */
  name: string;

  /**
   * User Email
   * @type {string}
   * @memberof DeviceModuleCreator
   */
  email: string;
}

/**
 * Device Module
 * @export
 * @interface DeviceModule
 */
export interface DeviceModule {
  /**
   * Module ID
   * @type {string}
   * @memberof DeviceModule
   */
  id?: string;

  /**
   * Module Type
   * @type {DeviceModuleTypeEnum}
   * @memberof DeviceModule
   */
  type: DeviceModuleTypeEnum;

  /**
   * Display Name
   * @type {string}
   * @memberof DeviceModule
   */
  displayName: string;

  /**
   * Display Order
   * @type {number}
   * @memberof DeviceModule
   */
  displayOrder: number;

  /**
   * Is Enabled
   * @type {boolean}
   * @memberof DeviceModule
   */
  isEnabled: boolean;

  /**
   * Device Types
   * @type {Array<DeviceModuleDeviceTypeEnum>}
   * @memberof DeviceModule
   */
  deviceTypes: Array<DeviceModuleDeviceTypeEnum>;

  /**
   * Module Configuration
   * @type {object}
   * @memberof DeviceModule
   */
  config?: object;

  /**
   * Created At
   * @type {string}
   * @memberof DeviceModule
   */
  createdAt?: string;

  /**
   * Updated At
   * @type {string}
   * @memberof DeviceModule
   */
  updatedAt?: string;

  /**
   * Created By
   * @type {DeviceModuleCreator}
   * @memberof DeviceModule
   */
  createdBy?: DeviceModuleCreator;
}

/**
 * Device Modules Response
 * @export
 * @interface DeviceModulesRsp
 */
export interface DeviceModulesRsp {
  /**
   * Success status
   * @type {boolean}
   * @memberof DeviceModulesRsp
   */
  success: boolean;

  /**
   * Modules list
   * @type {Array<DeviceModule>}
   * @memberof DeviceModulesRsp
   */
  modules: Array<DeviceModule>;
}

/**
 * Device Modules Create Request
 * @export
 * @interface DeviceModulesCreateReq
 */
export interface DeviceModulesCreateReq {
  /**
   * Modules to create
   * @type {Array<DeviceModule>}
   * @memberof DeviceModulesCreateReq
   */
  modules: Array<DeviceModule>;
}

/**
 * Available Module Type Enum (extends the existing enum)
 * @export
 */
export const AvailableModuleTypeEnum = {
  ...DeviceModuleTypeEnum,
  GuestPass: 'GUEST_PASS',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AvailableModuleTypeEnum =
  (typeof AvailableModuleTypeEnum)[keyof typeof AvailableModuleTypeEnum];

/**
 * Available Module Device Types Enum (extends the existing enum)
 * @export
 */
export const AvailableModuleDeviceTypeEnum = {
  ...DeviceModuleDeviceTypeEnum,
  Physical: 'PHYSICAL',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AvailableModuleDeviceTypeEnum =
  (typeof AvailableModuleDeviceTypeEnum)[keyof typeof AvailableModuleDeviceTypeEnum];

/**
 * Available Module
 * @export
 * @interface AvailableModule
 */
export interface AvailableModule {
  /**
   * Module Type
   * @type {AvailableModuleTypeEnum}
   * @memberof AvailableModule
   */
  type: AvailableModuleTypeEnum;

  /**
   * Display Name
   * @type {string}
   * @memberof AvailableModule
   */
  displayName: string;

  /**
   * Default Configuration
   * @type {object}
   * @memberof AvailableModule
   */
  defaultConfig: object;

  /**
   * Device Types
   * @type {Array<AvailableModuleDeviceTypeEnum>}
   * @memberof AvailableModule
   */
  deviceTypes: Array<AvailableModuleDeviceTypeEnum>;
}

/**
 * Available Modules Summary
 * @export
 * @interface AvailableModulesSummary
 */
export interface AvailableModulesSummary {
  /**
   * Total Modules
   * @type {number}
   * @memberof AvailableModulesSummary
   */
  totalModules: number;

  /**
   * Virtual Only Modules Count
   * @type {number}
   * @memberof AvailableModulesSummary
   */
  virtualOnly: number;

  /**
   * Physical Only Modules Count
   * @type {number}
   * @memberof AvailableModulesSummary
   */
  physicalOnly: number;

  /**
   * Shared Modules Count
   * @type {number}
   * @memberof AvailableModulesSummary
   */
  sharedModules: number;
}

/**
 * Available Modules Response
 * @export
 * @interface AvailableModulesRsp
 */
export interface AvailableModulesRsp {
  /**
   * Success status
   * @type {boolean}
   * @memberof AvailableModulesRsp
   */
  success: boolean;

  /**
   * Modules grouped by type
   * @type {object}
   * @memberof AvailableModulesRsp
   */
  modules: {
    /**
     * All available modules
     * @type {Array<AvailableModule>}
     */
    all: Array<AvailableModule>;

    /**
     * Virtual device modules
     * @type {Array<AvailableModule>}
     */
    virtual: Array<AvailableModule>;

    /**
     * Physical device modules
     * @type {Array<AvailableModule>}
     */
    physical: Array<AvailableModule>;
  };

  /**
   * Summary statistics
   * @type {AvailableModulesSummary}
   * @memberof AvailableModulesRsp
   */
  summary: AvailableModulesSummary;
}

/**
 * Device Logo Info
 * @export
 * @interface DeviceLogoInfo
 */
export interface DeviceLogoInfo {
  /**
   * Logo ID
   * @type {string}
   * @memberof DeviceLogoInfo
   */
  id?: string;

  /**
   * Logo URL
   * @type {string}
   * @memberof DeviceLogoInfo
   */
  logoUrl?: string;

  /**
   * Logo Filename
   * @type {string}
   * @memberof DeviceLogoInfo
   */
  logoFilename?: string;

  /**
   * Logo Uploaded At
   * @type {string}
   * @memberof DeviceLogoInfo
   */
  logoUploadedAt?: string;

  /**
   * Logo Uploaded By
   * @type {string}
   * @memberof DeviceLogoInfo
   */
  logoUploadedBy?: string;
}

/**
 * Device Logo Upload Response
 * @export
 * @interface DeviceLogoUploadRsp
 */
export interface DeviceLogoUploadRsp {
  /**
   * Success status
   * @type {boolean}
   * @memberof DeviceLogoUploadRsp
   */
  success: boolean;

  /**
   * Message
   * @type {string}
   * @memberof DeviceLogoUploadRsp
   */
  message: string;

  /**
   * Logo Info
   * @type {DeviceLogoInfo}
   * @memberof DeviceLogoUploadRsp
   */
  logo: DeviceLogoInfo;
}

/**
 * Device Logo Get Response
 * @export
 * @interface DeviceLogoGetRsp
 */
export interface DeviceLogoGetRsp {
  /**
   * Success status
   * @type {boolean}
   * @memberof DeviceLogoGetRsp
   */
  success: boolean;

  /**
   * Logo Info
   * @type {DeviceLogoInfo}
   * @memberof DeviceLogoGetRsp
   */
  logo: DeviceLogoInfo;
}

/**
 * Device Logo Delete Response
 * @export
 * @interface DeviceLogoDeleteRsp
 */
export interface DeviceLogoDeleteRsp {
  /**
   * Success status
   * @type {boolean}
   * @memberof DeviceLogoDeleteRsp
   */
  success: boolean;

  /**
   * Message
   * @type {string}
   * @memberof DeviceLogoDeleteRsp
   */
  message: string;
}

/**
 * Room Sort By Enum
 * @export
 */
export const RoomSortByEnum = {
  RoomName: 'roomName',
  FirstName: 'firstName',
  LastName: 'lastName',
  FloorName: 'floorName',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type RoomSortByEnum = (typeof RoomSortByEnum)[keyof typeof RoomSortByEnum];

/**
 * Room Display Format Enum
 * @export
 */
export const RoomDisplayFormatEnum = {
  RoomName: 'roomName',
  TenantName: 'tenantName',
  TenantNameLastFirst: 'tenantNameLastFirst',
  Both: 'both',
  BothLastFirst: 'bothLastFirst',
  RoomNameWithFloor: 'roomNameWithFloor',
  TenantNameWithFloor: 'tenantNameWithFloor',
  TenantNameLastFirstWithFloor: 'tenantNameLastFirstWithFloor',
  BothWithFloor: 'bothWithFloor',
  BothLastFirstWithFloor: 'bothLastFirstWithFloor',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type RoomDisplayFormatEnum =
  (typeof RoomDisplayFormatEnum)[keyof typeof RoomDisplayFormatEnum];

/**
 * Room Configuration
 * @export
 * @interface RoomConfig
 */
export interface RoomConfig {
  /**
   * Device ID
   * @type {string}
   * @memberof RoomConfig
   */
  id: string;

  /**
   * Room Sort By
   * @type {RoomSortByEnum}
   * @memberof RoomConfig
   */
  roomSortBy?: RoomSortByEnum;

  /**
   * Room Display Format
   * @type {RoomDisplayFormatEnum}
   * @memberof RoomConfig
   */
  roomDisplayFormat?: RoomDisplayFormatEnum;
}

/**
 * Room Configuration Update Request
 * @export
 * @interface RoomConfigUpdateReq
 */
export interface RoomConfigUpdateReq {
  /**
   * Room Sort By
   * @type {RoomSortByEnum}
   * @memberof RoomConfigUpdateReq
   */
  roomSortBy?: RoomSortByEnum;

  /**
   * Room Display Format
   * @type {RoomDisplayFormatEnum}
   * @memberof RoomConfigUpdateReq
   */
  roomDisplayFormat?: RoomDisplayFormatEnum;
}

/**
 * Room Configuration Response
 * @export
 * @interface RoomConfigRsp
 */
export interface RoomConfigRsp {
  /**
   * Success status
   * @type {boolean}
   * @memberof RoomConfigRsp
   */
  success: boolean;

  /**
   * Message (for update response)
   * @type {string}
   * @memberof RoomConfigRsp
   */
  message?: string;

  /**
   * Device configuration
   * @type {RoomConfig}
   * @memberof RoomConfigRsp
   */
  device: RoomConfig;
}
